/**
 * Scroll-spy for the single-post sidebar Table of Contents.
 *
 * Watches each heading the TOC links to (#slug) via IntersectionObserver
 * and toggles `.is-active` on the matching .tx-post__toc-item so the
 * sidebar mirrors the reader's position in the article.
 *
 * The root-margin band (-80px top, -60% bottom) means a heading counts as
 * "active" once it has crossed the top ~80px of the viewport and stays
 * active until the next one does the same — feels natural during scroll.
 */
const init = () => {
    const toc = document.querySelector('.tx-post__toc');
    if (!toc) return;

    const items = new Map();
    toc.querySelectorAll('a[href^="#"]').forEach((link) => {
        const id = link.getAttribute('href').slice(1);
        const item = link.closest('.tx-post__toc-item');
        if (id && item) items.set(id, item);
    });
    if (!items.size) return;

    const headings = [...items.keys()]
        .map((id) => document.getElementById(id))
        .filter(Boolean);
    if (!headings.length) return;

    const setActive = (id) => {
        items.forEach((item, key) => {
            item.classList.toggle('is-active', key === id);
        });
    };

    // Track which headings are currently inside the active band; the
    // top-most one wins. Observer entries don't always include every
    // heading on each callback, so we keep a running set.
    const intersecting = new Set();

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) intersecting.add(e.target);
                else intersecting.delete(e.target);
            });

            if (!intersecting.size) return;
            const top = [...intersecting].sort(
                (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
            )[0];
            if (top && top.id) setActive(top.id);
        },
        { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    );

    headings.forEach((h) => observer.observe(h));

    // Default-active: first heading until scroll fires.
    if (headings[0] && headings[0].id) setActive(headings[0].id);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
